/**
 * UI-8a Task 3 — ProvisionStepReview.
 *
 * Step 4 (final) of the tenant provisioning wizard. Renders a read-only
 * summary of everything the operator entered, then dispatches the three
 * lifecycle mutations in sequence when "Provision" is clicked:
 *
 *   1. setPhase('creating')      → create({ slug, name, planId })
 *   2. setPhase('provisioning')  → provision(tenant.id)
 *   3. setPhase('admin')         → provisionAdminUser(tenant.id, …)
 *   4. setPhase('done')          → onComplete(tenant)
 *
 * Failures stop the chain and surface the error message; the operator can
 * close the drawer and either retry from the detail page (if the tenant
 * row was created before the failure) or restart the wizard.
 *
 * Progress label updates per phase and lives inside an aria-live="polite"
 * region for screen-reader announcements during the multi-step flow.
 */
import { useState, useImperativeHandle, forwardRef } from 'react'
import {
  useTenants,
  type Tenant,
} from '../../../hooks/useTenants.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { api } from '../../../api/client.js'
import { DEFAULT_TENANT_RECORDING_CONFIG, type TenantRecordingConfigInput } from '@kc3/contracts'
import { validateRecording, recordingChanged } from './ProvisionStepRecording.js'

export interface ProvisionWizardData {
  slug: string
  name: string
  planId: string | null
  status: 'active' | 'suspended'
  // Basic Info configuration
  domainUrl?: string
  timeZone?: string
  userLimitation?: string
  // Client Details
  clientFullName?: string
  clientEmail?: string
  clientMobileNumber?: string
  clientLandline?: string
  clientAddress?: string
  // Company Details
  companyName?: string
  companyEmail?: string
  // AWS S3
  enableS3?: boolean
  awss3BucketName?: string
  awss3AccessKey?: string
  awss3Secret?: string
  awss3Region?: string
  awss3UrlExpiry?: number
  // Post-call analytics (AWS Transcribe). The secret is write-only: blank
  // means "keep the stored one".
  postCallAnalytics?: boolean
  awsTranscribeAccessKey?: string
  awsTranscribeSecret?: string
  awsTranscribeRegion?: string
  awsTranscribeS3Bucket?: string
  awsTranscribeLanguageCode?: string
  awsTranscribeIamArn?: string
  // Agent-facing: upload external recordings into Call History.
  manualCallHistoryUpload?: boolean
  showAgentSentiment?: boolean
  // Live call assist engine; the Whisper key is write-only like the AWS secret.
  liveAssistEngine?: 'aws' | 'self_hosted'
  liveAssistWhisperUrl?: string
  liveAssistWhisperKey?: string
  liveAssistModel?: string
  // Feature flags
  featureFlags?: Record<string, boolean>
  // Company type and default seed toggle
  companyType?: string
  seedDefaultData?: boolean
  // Super Admin recording settings (saved via a separate endpoint)
  recording?: TenantRecordingConfigInput
}

export interface ProvisionStepReviewProps {
  data: ProvisionWizardData
  timezones?: { id: string; label: string; value: string; createdAt: string }[]
  onComplete: (tenant: Tenant) => void
  mode?: 'create' | 'view' | 'edit'
  tenant?: Tenant | undefined
  // Feature checkbox states
  callAnalytics?: boolean
  googleSignIn?: boolean
  googleClientId?: string
  googleClientSecret?: string
  microsoftSignIn?: boolean
  microsoftClientId?: string
  microsoftClientSecret?: string
  continueRecording?: boolean
  transferFeatures?: Record<string, boolean>
}

export interface ProvisionStepReviewRef {
  handleProvision: () => void
  inFlight: boolean
  phase: string
}

type Phase = 'idle' | 'creating' | 'provisioning' | 'done' | 'error'

/** Transcription fields for the create/update body. A blank secret is omitted so the stored one is kept. */
function transcriptionBody(data: ProvisionWizardData): Record<string, unknown> {
  const secret = data.awsTranscribeSecret?.trim() ?? ''
  const whisperKey = data.liveAssistWhisperKey?.trim() ?? ''
  return {
    liveAssistEngine: data.liveAssistEngine ?? 'aws',
    liveAssistWhisperUrl: data.liveAssistWhisperUrl?.trim() || null,
    liveAssistModel: data.liveAssistModel?.trim() || null,
    ...(whisperKey ? { liveAssistWhisperKey: whisperKey } : {}),
    postCallAnalytics: data.postCallAnalytics ?? false,
    awsTranscribeAccessKey: data.awsTranscribeAccessKey?.trim() || null,
    awsTranscribeRegion: data.awsTranscribeRegion || null,
    awsTranscribeS3Bucket: data.awsTranscribeS3Bucket?.trim() || null,
    awsTranscribeLanguageCode: data.awsTranscribeLanguageCode || 'en-US',
    awsTranscribeIamArn: data.awsTranscribeIamArn?.trim() || null,
    manualCallHistoryUpload: data.manualCallHistoryUpload ?? false,
    showAgentSentiment: data.showAgentSentiment ?? true,
    ...(secret ? { awsTranscribeSecret: secret } : {}),
  }
}

const _PHASE_LABEL: Record<Phase, string> = {
  idle: '',
  creating: 'Creating tenant…',
  provisioning: 'Provisioning schema…',
  done: 'Done.',
  error: '',
}

export const ProvisionStepReview = forwardRef<ProvisionStepReviewRef, ProvisionStepReviewProps>(function ProvisionStepReview({
  data,
  timezones,
  onComplete,
  mode = 'create',
  tenant,
  callAnalytics = false,
  googleSignIn = false,
  googleClientId = '',
  googleClientSecret = '',
  microsoftSignIn = false,
  microsoftClientId = '',
  microsoftClientSecret = '',
  continueRecording = false,
  transferFeatures = {},
}, ref) {
  const { create, update, provision } = useTenants()
  const toast = useToast()
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)

  const inFlight =
    phase === 'creating' || phase === 'provisioning'

  useImperativeHandle(ref, () => ({
    handleProvision: () => {
      void handleProvisionInternal()
    },
    get inFlight() {
      return inFlight
    },
    get phase() {
      return phase
    },
  }))

  async function handleProvisionInternal(): Promise<void> {
    setError(null)
    try {
      if (data.domainUrl) {
        const trimmedUrl = data.domainUrl.trim()
        if (!trimmedUrl) {
          setError('Domain URL cannot be empty or only spaces')
          return
        }
        try {
          const urlWithProtocol = trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`
          const urlObj = new URL(urlWithProtocol)
          if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            setError('Domain URL must use http or https protocol')
            return
          }
          const hostname = urlObj.hostname
          if (!hostname || (!hostname.includes('.') && hostname !== 'localhost')) {
            setError('Invalid domain. Please enter a valid domain (e.g., example.com or https://example.com)')
            return
          }
        } catch {
          setError('Invalid URL format. Please enter a valid URL (e.g., https://example.com)')
          return
        }
      }

      if (data.recording && Object.keys(validateRecording(data.recording)).length > 0) {
        setError('Recording configuration is invalid. Fix it on the Recording step.')
        return
      }

      const featureFlags: Record<string, any> = {
        callAnalytics,
        googleSignIn: googleSignIn ? {
          enabled: true,
          clientId: googleClientId,
          clientSecret: googleClientSecret,
        } : false,
        microsoftSignIn: microsoftSignIn ? {
          enabled: true,
          clientId: microsoftClientId,
          clientSecret: microsoftClientSecret,
        } : false,
        continueRecording,
        ...transferFeatures,
      }

      if (mode === 'edit' && tenant) {
        setPhase('creating')
        const updateBody: Record<string, unknown> = { name: data.name, status: data.status, featureFlags }
        if (data.planId) updateBody.planId = data.planId
        if (data.domainUrl) {
          const urlWithProtocol = data.domainUrl.startsWith('http') ? data.domainUrl : `https://${data.domainUrl}`
          updateBody.domainUrl = urlWithProtocol
        }
        if (data.timeZone) updateBody.timeZone = data.timeZone
        if (data.userLimitation) updateBody.userLimitation = data.userLimitation
        if (data.clientFullName) updateBody.clientFullName = data.clientFullName
        if (data.clientEmail) updateBody.clientEmail = data.clientEmail
        if (data.clientMobileNumber) updateBody.clientMobileNumber = data.clientMobileNumber
        if (data.clientLandline) updateBody.clientLandline = data.clientLandline
        if (data.clientAddress) updateBody.clientAddress = data.clientAddress
        if (data.companyName) updateBody.companyName = data.companyName
        if (data.companyEmail) updateBody.companyEmail = data.companyEmail
        if (data.companyType) updateBody.companyType = data.companyType
        if (data.seedDefaultData !== undefined) updateBody.seedDefaultData = data.seedDefaultData
        if (data.enableS3 !== undefined) updateBody.enableS3 = data.enableS3
        if (data.awss3BucketName !== undefined) updateBody.awss3BucketName = data.awss3BucketName
        if (data.awss3AccessKey !== undefined) updateBody.awss3AccessKey = data.awss3AccessKey
        if (data.awss3Secret !== undefined) updateBody.awss3Secret = data.awss3Secret
        if (data.awss3Region !== undefined) updateBody.awss3Region = data.awss3Region
        if (data.awss3UrlExpiry !== undefined) updateBody.awss3UrlExpiry = data.awss3UrlExpiry
        Object.assign(updateBody, transcriptionBody(data))

        const updated = await update(tenant.id, updateBody)
        // Tenant update already succeeded: a recording failure must not leave a
        // Retry that re-runs the update, so warn instead.
        if (
          data.recording &&
          recordingChanged(data.recording, tenant.recordingConfig ?? DEFAULT_TENANT_RECORDING_CONFIG)
        ) {
          try {
            await api.put(`/platform/tenants/${tenant.id}/recording-config`, data.recording)
          } catch {
            toast.warning('Tenant updated, but the recording settings could not be saved. Edit the tenant to set them again.')
          }
        }
        setPhase('done')
        onComplete(updated)
      } else {
        setPhase('creating')
        const newTenant = await create({
          slug: data.slug,
          name: data.name,
          status: data.status,
          ...(data.planId ? { planId: data.planId } : {}),
          ...(data.domainUrl ? { domainUrl: data.domainUrl } : {}),
          ...(data.timeZone ? { timeZone: data.timeZone } : {}),
          ...(data.userLimitation ? { userLimitation: data.userLimitation } : {}),
          ...(data.clientFullName ? { clientFullName: data.clientFullName } : {}),
          ...(data.clientEmail ? { clientEmail: data.clientEmail } : {}),
          ...(data.clientMobileNumber ? { clientMobileNumber: data.clientMobileNumber } : {}),
          ...(data.clientLandline ? { clientLandline: data.clientLandline } : {}),
          ...(data.clientAddress ? { clientAddress: data.clientAddress } : {}),
          ...(data.companyName ? { companyName: data.companyName } : {}),
          ...(data.companyEmail ? { companyEmail: data.companyEmail } : {}),
          companyType: data.companyType ?? 'general',
          seedDefaultData: data.seedDefaultData ?? false,
          ...(data.enableS3 !== undefined ? { enableS3: data.enableS3 } : {}),
          ...(data.awss3BucketName !== undefined ? { awss3BucketName: data.awss3BucketName } : {}),
          ...(data.awss3AccessKey !== undefined ? { awss3AccessKey: data.awss3AccessKey } : {}),
          ...(data.awss3Secret !== undefined ? { awss3Secret: data.awss3Secret } : {}),
          ...(data.awss3Region !== undefined ? { awss3Region: data.awss3Region } : {}),
          ...(data.awss3UrlExpiry !== undefined ? { awss3UrlExpiry: data.awss3UrlExpiry } : {}),
          ...transcriptionBody(data),
          featureFlags,
        })

        setPhase('provisioning')
        await provision(newTenant.id)
        // Tenant already exists and is provisioned: a recording failure must not
        // put the wizard into the error/Retry state (Retry would re-create it).
        // GET returns defaults when there is no row, so skip the PUT for defaults.
        if (data.recording && recordingChanged(data.recording, DEFAULT_TENANT_RECORDING_CONFIG)) {
          try {
            await api.put(`/platform/tenants/${newTenant.id}/recording-config`, data.recording)
          } catch {
            toast.warning('Tenant created, but the recording settings could not be saved. Edit the tenant to set them.')
          }
        }

        setPhase('done')
        onComplete(newTenant)
      }
    } catch (err) {
      setPhase('error')
      const errorMessage = err instanceof Error ? err.message : 'Provisioning failed.'
      setError(errorMessage)
      toast.error(errorMessage)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6">
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <i className="pi pi-exclamation-triangle text-red-500"></i>
              <span className="text-sm font-medium text-red-700">{error}</span>
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Review Setup</h3>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 text-sm">
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Tenant Name</dt>
                <dd className="mt-1.5 font-semibold text-gray-900">{data.name || 'N/A'}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Slug</dt>
                <dd className="mt-1.5 font-mono text-gray-700 bg-gray-50 px-2.5 py-1 rounded-md inline-block w-max text-xs">{data.slug}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Status</dt>
                <dd className="mt-1.5">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${data.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-600'}`}>
                    {data.status === 'active' ? 'Active' : 'Suspended'}
                  </span>
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Domain</dt>
                <dd className="mt-1.5 text-gray-700">{data.domainUrl || 'N/A'}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Time Zone</dt>
                <dd className="mt-1.5 text-gray-700">
                  {(timezones?.find((tz) => tz.value === data.timeZone)?.label ?? data.timeZone) || 'UTC'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">User Limitation</dt>
                <dd className="mt-1.5 text-gray-700">{data.userLimitation || 'N/A'}</dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Post Call Analytics</dt>
                <dd className="mt-1.5 text-gray-700">
                  {data.postCallAnalytics
                    ? `Enabled — AWS Transcribe (${data.awsTranscribeRegion || 'no region'}, ${data.awsTranscribeLanguageCode || 'en-US'})`
                    : 'Disabled'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Recording</dt>
                <dd className="mt-1.5 text-gray-700">
                  {data.recording?.recordingEnabled
                    ? `Enabled · ${data.recording.recordingType}${data.recording.retentionDays ? ` · ${data.recording.retentionDays}d retention` : ''}`
                    : 'Disabled'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Live Call Assist Engine</dt>
                <dd className="mt-1.5 text-gray-700">
                  {data.liveAssistEngine === 'self_hosted'
                    ? `Self-hosted — Whisper (${data.liveAssistWhisperUrl || 'no URL'}) + ${data.liveAssistModel || 'ollama:gemma4:e4b'}`
                    : 'AWS Transcribe + cloud AI routing'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Manual Call History Upload</dt>
                <dd className="mt-1.5 text-gray-700">
                  {data.manualCallHistoryUpload
                    ? 'Enabled — agents can upload external recordings into Call History'
                    : 'Disabled'}
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="font-medium text-gray-400 text-xs uppercase tracking-wider">Show Agent Sentiment</dt>
                <dd className="mt-1.5 text-gray-700">
                  {data.showAgentSentiment === false ? 'Hidden on QM dashboards' : 'Shown on QM dashboards'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
})

export default ProvisionStepReview
